/**
 * Sync Initializer
 *
 * ISSUE_038: Initializes sync engine in a Web Worker for off-main-thread sync.
 * This component should be rendered inside AuthProvider.
 *
 * Uses Web Workers to prevent main thread blocking with 100k+ sessions.
 *
 * @module core/sync/SyncInitializer
 */

import { useEffect } from "react";
import { persistentToast } from "~/core/utils/persistentToast";
import { useWorkspace } from "~/core/hooks/useWorkspace";
import { useSyncEnabled } from "~/core/store/useSyncStore";
import { useSyncStatusStore } from "~/core/store/useSyncStatusStore";
import type { WorkspaceContext } from "~/services/sessions/sessionsService";
import { getSession } from "~/shared/lib/supabase";
import {
  getSyncWorkerClient,
  hasSyncWorkerClient,
  clearSyncWorkerClient,
  clearSyncWorkerClientImmediate,
} from "./workerClient";
import { touchSyncSuccess } from "~/core/resilience";
import { formatTimeFromMs } from "~/core/utils/timeFormat";
import { localDb } from "~/db/client";
// ISSUE_047: Use AuthBroker for token refresh
import { refreshAuthToken } from "~/core/auth/AuthBroker";

// ISSUE_187: 현재 sync worker가 물고 있는 학원 ID (StrictMode 리마운트 생존 +
// 학원 전환 재조준 키). null = worker 미초기화. targetAcademyId와 다르면 학원이
// 바뀐 것 → 옛 worker 즉시 종료 후 재조준.
let initializedAcademyId: string | null = null;

/**
 * ISSUE_195 D5: 현재 worker 가 읽기 전용(pull only)으로 떠 있는지.
 * 구독이 로드되며 무료→프리미엄으로 바뀌면 같은 학원이라도 **재초기화**해야
 * push 가 살아난다(반대 방향도 마찬가지). 학원 ID 만으로 판단하면 모드가 굳는다.
 */
let initializedReadOnly: boolean | null = null;

/**
 * Reset the init state (call on logout to allow re-initialization)
 */
export function resetSyncInitializer(): void {
  initializedAcademyId = null;
  initializedReadOnly = null;
}

/**
 * Initializes the sync engine in a Web Worker when workspace is available.
 * Renders nothing - just manages the sync worker lifecycle.
 *
 * @example
 * // In root.tsx, inside AuthProvider:
 * <AuthProvider>
 *   <SyncInitializer />
 *   <Outlet />
 * </AuthProvider>
 */
export function SyncInitializer(): null {
  const workspace = useWorkspace();
  const syncEnabled = useSyncEnabled();

  useEffect(() => {
    // Skip on server
    if (typeof window === "undefined") {
      return;
    }

    // Skip if no valid workspace
    if (!workspace.hasValidWorkspace) {
      return;
    }

    // ISSUE_195 D5: 미구독이어도 worker 를 **읽기 전용(pull only)** 으로 띄운다.
    // 예전엔 여기서 통째로 return 해서, 구독이 만료되면 서버에 멀쩡히 남아 있는
    // 자기 수업 일정을 새 기기에서 영영 볼 수 없었다(실측 8,366건 열람 불가).
    // pull 만으로는 다기기 "쓰기" 동기화가 성립하지 않으므로 BM 은 유지된다 —
    // 로컬 변경은 pending 으로 쌓여 있다가 재구독 시 올라간다(D2).
    const readOnly = !syncEnabled;

    // ISSUE_187: 동기화 대상 학원 (hasValidWorkspace 가드로 non-null 보장)
    const targetAcademyId = workspace.academyId;
    if (!targetAcademyId) {
      return;
    }

    // 같은 학원 + 같은 모드로 이미 초기화됨 → skip (StrictMode 리마운트 안전)
    if (
      hasSyncWorkerClient() &&
      initializedAcademyId === targetAcademyId &&
      initializedReadOnly === readOnly
    ) {
      return;
    }

    // ISSUE_187: 학원이 바뀜(또는 stale worker) → 옛 worker 즉시 종료하고 재조준.
    // (이전엔 싱글톤 + isInitialized 가드가 학원 전환을 막아 worker가 옛 학원을
    //  계속 물고 → 새 학원 데이터 미동기화 + 크로스-학원 누수 위험이 있었음.)
    // ISSUE_195 D5: 결제로 readOnly 가 풀린 경우도 같은 이유로 재조준해야 push 가 산다.
    if (hasSyncWorkerClient()) {
      clearSyncWorkerClientImmediate();
    }

    // Get Supabase config from env
    const supabaseUrl = import.meta.env["VITE_SUPABASE_URL"];
    const supabaseAnonKey = import.meta.env["VITE_SUPABASE_ANON_KEY"];

    if (!supabaseUrl || !supabaseAnonKey) {
      return;
    }

    // Convert to WorkspaceContext format
    // ISSUE_183: 워크스페이스 = academy 단일(ISSUE_182). 위 가드가 hasValidWorkspace 를 요구하고
    // hasValidWorkspace===isAcademyWorkspace(둘 다 !!academyId)이므로 여기선 항상 academy —
    // 죽은 scratch 분기 제거. scratch context 가 생성되지 않으므로 하위 sync 엔진의 scratch 경로도
    // 전부 도달 불가(안전한 죽은 코드).
    const workspaceContext: WorkspaceContext = { type: "academy", academyId: workspace.academyId! };

    // Get access token for RLS authentication
    const startWorker = async () => {
      const session = await getSession();
      if (!session?.access_token) {
        return;
      }

      // Get sync status store actions
      const { updateProgress, markInitialSyncSeen } =
        useSyncStatusStore.getState();

      // ISSUE_187: 이 학원 동기화 시작 — 진행률 리셋 + academyId 스탬프
      // (런처 카드가 자기 학원 게이지인지 식별 / 전환 시 이전 학원 진행률 잔존 방지)
      useSyncStatusStore.getState().beginSync(targetAcademyId);

      // Get singleton worker client and start
      const client = getSyncWorkerClient({
        workspace: workspaceContext,
        readOnly, // ISSUE_195 D5
        supabaseUrl,
        supabaseAnonKey,
        accessToken: session.access_token,
        autoSyncInterval: 30000, // 30 seconds
        onSyncStateChange: (state) => {
          // Report to HealthMonitor for resilience system
          if (state.lastSyncAt) {
            touchSyncSuccess();
          }
        },
        onSyncProgress: (progress) => {
          updateProgress(progress);

          // Mark initial sync as seen when complete
          if (progress.phase === "complete" && progress.pulled > 1000) {
            markInitialSyncSeen();
          }
        },
        onError: (error) => {
          console.error("[SyncInitializer] Sync error:", error);
        },
        // ISSUE_047: Token refresh callback using AuthBroker (single-flight)
        // AuthBroker가 진짜 만료(refresh token 만료)는 forceLogout()으로 처리.
        // 여기서 null이면 일시적 실패 — 토스트 없이 로그만 남김.
        onTokenRefreshRequired: async () => {
          try {
            const { token } = await refreshAuthToken();
            if (!token) {
              // AuthBroker handles true expiration via forceLogout()
            }
            return token;
          } catch (err) {
            console.error("[SyncInitializer] Token refresh error:", err);
            return null;
          }
        },
        onPushError: async ({ sessionId, studentName, error }) => {
          console.error("[SyncInitializer] Push error:", { sessionId, studentName, error });
          // Show user-friendly toast for constraint violations (longer duration)
          persistentToast("error", `${studentName}: ${error}`);

          // Get session details from IndexedDB for error log
          const session = await localDb.sessions.get(sessionId);

          // Add to Error Log Store (ISSUE_046)
          const { useErrorLogStore } = await import("~/core/store/useErrorLogStore");
          useErrorLogStore.getState().addError({
            sessionId,
            studentName,
            ...(session?.teacherName && { teacherName: session.teacherName }),
            time: session
              ? `${formatTimeFromMs(session.startMs)}~${formatTimeFromMs(session.endMs)}`
              : "",
            error,
          });

          // Update store to show error state in UI
          const { useSessionsStore } = await import("~/core/store/useSessionsStore");
          useSessionsStore.getState().setSessionError(sessionId, error);
        },
      });

      initializedAcademyId = targetAcademyId;
      initializedReadOnly = readOnly; // ISSUE_195 D5
      void client.start();
    };

    void startWorker();

    // Cleanup on workspace change
    return () => {
      // Don't stop during StrictMode - the remount will check hasSyncWorkerClient()
    };
  }, [
    syncEnabled,
    workspace.hasValidWorkspace,
    workspace.isAcademyWorkspace,
    workspace.academyId,
    workspace.ownerId,
  ]);

  // Cleanup on full unmount (page close)
  useEffect(() => {
    return () => {
      // Only cleanup on actual page close
      if (typeof window !== "undefined") {
        window.addEventListener("beforeunload", () => {
          void clearSyncWorkerClient();
        });
      }
    };
  }, []);

  return null;
}

export default SyncInitializer;
